import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import { getCurrentUser } from "../services/pocketbase";

const PasswordConfessional = () => {
  const [open, setOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const user = getCurrentUser();

  useEffect(() => {
    const handleInput = (e) => {
      // Check if the input value contains the trigger phrase (case insensitive)
      if (
        e.target.tagName === "INPUT" &&
        e.target.value &&
        e.target.value.toLowerCase().includes("i reuse passwords")
      ) {
        setOpen(true);
      }
    };

    // Listen for input events on all input fields
    document.addEventListener("input", handleInput);

    return () => {
      document.removeEventListener("input", handleInput);
    };
  }, []);

  const handleClose = () => {
    setOpen(false);
    setSuccess(false);
    setError(null);
  };

  const assignDiscordRole = async () => {
    if (!user?.discord_id) {
      setError("You need to link your Discord account first!");
      return;
    }

    setAssigning(true);
    setError(null);

    try {
      const response = await fetch("/api/discord/assign-role", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          discordId: user.discord_id,
          roleId: "1372892605947576370", // Security Awareness role ID
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.details || data.error || "Failed to assign role");
      }

      setSuccess(true);
    } catch (err) {
      console.error("Error assigning role:", err);
      setError(err.message || "Failed to assign Discord role");
    } finally {
      setAssigning(false);
    }
  };

  useEffect(() => {
    // Assign role automatically when dialog opens if user has Discord ID
    if (open && user?.discord_id && !success && !error && !assigning) {
      assignDiscordRole();
    }
    // eslint-disable-next-line
  }, [open, user?.discord_id, success, error, assigning]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#300",
          border: "2px solid red",
          boxShadow: "0 0 20px rgba(255, 0, 0, 0.7)",
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "red",
          color: "white",
          fontWeight: "bold",
          textAlign: "center",
          animation: "pulse 1.5s infinite",
          "@keyframes pulse": {
            "0%": { opacity: 0.7 },
            "50%": { opacity: 1 },
            "100%": { opacity: 0.7 },
          },
        }}
      >
        SECURITY BREACH DETECTED
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <Typography variant="h6" color="error" gutterBottom>
          Password Reuse Is A Serious Security Risk!
        </Typography>

        <Typography variant="body1" color="white" paragraph>
          Reusing passwords across multiple sites is one of the most dangerous
          security practices. If one site is compromised, all your accounts
          become vulnerable.
        </Typography>

        <Typography variant="body1" color="white" paragraph>
          Consider using a password manager to generate and store unique, strong
          passwords for each site you use.
        </Typography>

        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" color="white" gutterBottom>
            Recommended Password Managers:
          </Typography>
          <ul style={{ color: "white" }}>
            <li>Bitwarden (Free, Open Source)</li>
            <li>1Password</li>
            <li>LastPass</li>
            <li>KeePassXC (Offline, Open Source)</li>
          </ul>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Security Awareness role assigned to your Discord account!
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
        <Button
          onClick={handleClose}
          variant="contained"
          color="error"
          sx={{ fontWeight: "bold" }}
        >
          I UNDERSTAND THE RISK
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PasswordConfessional;
